// src/auth/PermissionsContext.jsx
import React, { createContext, useMemo, useContext } from "react";
import { useAuthContext } from "./useAuthContext";

const PermissionsContext = createContext(null);

const toSet = (arr) => new Set(Array.isArray(arr) ? arr.filter(Boolean) : []);

/**
 * Normalize abilities - تحويل abilities للصيغة المتوافقة
 * @param {Array} abilities - abilities من الـ API
 * @returns {Set} - Set of normalized permissions
 */
const normalizeAbilities = (abilities = []) => {
    const abilitiesArray = Array.isArray(abilities) ? abilities : [];

    // إذا كان admin:* موجود، يعني لديه كل الصلاحيات
    if (abilitiesArray.includes('admin:*')) {
        return new Set(['*']); // كل الصلاحيات
    }

    // تحويل abilities لصيغة permissions_names المستخدمة في routePermissions
    // مثال: 'products:create' => 'products_create'
    const normalized = abilitiesArray.map(ability => {
        if (ability.includes(':')) {
            return ability.replace(':', '_');
        }
        return ability;
    });

    return toSet(normalized);
};

export function PermissionsProvider({ children }) {
    const { user, isAuthenticated, isInitialized } = useAuthContext();

    // استخدام abilities من user object (في الذاكرة)
    // سيتم ملؤها من localStorage في JwtContext.js
    const abilities = useMemo(() => {
        // محاولة الحصول على abilities من user object أولاً
        if (user?.abilities) {
            return user.abilities;
        }

        // أو من localStorage كخطة احتياطية
        try {
            const storedAbilities = localStorage.getItem('abilities');
            return storedAbilities ? JSON.parse(storedAbilities) : [];
        } catch {
            return [];
        }
    }, [user]);

    const permissionsSet = useMemo(() => normalizeAbilities(abilities), [abilities]);

    const api = useMemo(() => {
        const has = (perm) => {
            // إذا كان لديه كل الصلاحيات
            if (permissionsSet.has('*')) {
                return true;
            }
            return permissionsSet.has(perm);
        };

        const hasAny = (perms = []) => {
            if (permissionsSet.has('*')) {
                return true;
            }
            return perms.some((p) => permissionsSet.has(p));
        };

        const hasAll = (perms = []) => {
            if (permissionsSet.has('*')) {
                return true;
            }
            return perms.every((p) => permissionsSet.has(p));
        };

        const hasPrefix = (prefix) => {
            if (permissionsSet.has('*')) {
                return true;
            }
            for (const p of permissionsSet) {
                if (p.startsWith(prefix)) return true;
            }
            return false;
        };

        // helper عام: can("countries", "create") => "countries_create"
        const can = (resource, action) => has(`${resource}_${action}`);

        return {
            isInitialized,
            isAuthenticated,
            permissionsSet,
            abilities, // raw abilities
            has,
            hasAny,
            hasAll,
            hasPrefix,
            can,
            user,
        };
    }, [permissionsSet, abilities, isAuthenticated, isInitialized, user]);

    return <PermissionsContext.Provider value={api}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
    const ctx = useContext(PermissionsContext);
    if (!ctx) throw new Error("usePermissions must be used within PermissionsProvider");
    return ctx;
}
